import { getEmptyOffsetPagedResults, OffsetPagedResults } from '~interfaces';
import { SkcOffsetPagedResponse } from '~interfaces/responses';
import SkopeiConnectService from '~services/skopeiConnectService';

import OfflineKey from '../../../interfaces/offlineKey';

class OfflineAccessService extends SkopeiConnectService {
	protected readonly path = 'keys/offline';

	/**
	 * Get a list of offline keys.
	 *
	 * @param page The page.
	 * @param pageSize The page size.
	 * @param searchQuery Text to search for in the offline key.
	 * @param deviceModelName Filter on device model name.
	 * @param resourceGroupId Filter on resource group id.
	 * @param deviceIds The device ids to filter on.
	 * @param keyIds The key ids to filter on.
	 * @param signal Signal for aborting the call.
	 *
	 * @returns An array of Offline keys
	 */
	async getOfflineKeys(
		params: {
			page: string;
			pageSize: string;
			searchQuery?: string;
			organisationId?: string;
			resourceGroupId?: string;
			deviceIds?: string[];
			keyIds?: string[];
		},
		signal?: AbortSignal
	): Promise<OffsetPagedResults<OfflineKey>> {
		const endpoint = this.createEndpoint('v2', [], true);
		return await this.client
			.get<SkcOffsetPagedResponse<OfflineKeyResponse>>(endpoint, {
				signal: signal,
				params: {
					'page-number': params.page,
					'page-size': params.pageSize,
					'client-id': params.organisationId,
					'resource-group-id': params.resourceGroupId,
					search: params.searchQuery?.trim() || undefined,
					'device-ids': params.deviceIds?.join(',') || undefined,
					'key-ids': params.keyIds?.join(',') || undefined,
				},
			})
			.then((response) => {
				if (!response.data) {
					return Promise.resolve(
						getEmptyOffsetPagedResults<OfflineKey>(Number(params.page), Number(params.pageSize))
					);
				}
				return Promise.resolve(
					this.mapSkcOffsetPagedResponse(response.data, OfflineAccessService.fromResponse)
				);
			});
	}

	public getOfflineKeysCacheKey(
		searchParams: URLSearchParams,
		source: string,
		refreshTrigger: number,
		deviceId?: string,
		keyId?: string,
		overwrittenClientId?: string
	) {
		return [
			this.path,
			searchParams.toString(),
			source,
			deviceId,
			keyId,
			overwrittenClientId,
			refreshTrigger,
		] as const;
	}

	/**
	 * Post an offline key.
	 *
	 * @param keyId id of the key to add.
	 * @param deviceId id of the device to add the key too.
	 */
	async postOfflineKey(
		params: { keyId: string; deviceId: string },
		signal?: AbortSignal
	): Promise<void> {
		const endpoint = this.createEndpoint('v2', [], true);
		return await this.client.post(
			endpoint,
			{
				keyId: params.keyId,
				deviceId: params.deviceId,
			},
			{
				signal: signal,
			}
		);
	}

	/**
	 * Delete an offline key.
	 *
	 * @param id The id of the offline key.
	 */
	async deleteOfflineKey(id: string): Promise<void> {
		const endpoint = this.createEndpoint('v2', [id], true);
		return await this.client.delete(endpoint);
	}

	/**
	 * Model parsing.
	 */
	private static fromResponse(data: OfflineKeyResponse): OfflineKey {
		return {
			id: data.id.toString(),
			key: {
				id: data.key.id.toString(),
				serialNumber: data.key.serialNumber ?? undefined,
				name: data.key.name,
			},
			device: {
				id: data.deviceHeader.id.toString(),
				hardwareId: data.deviceHeader.hardwareId,
				name: data.deviceHeader.name,
			},
			isDeviceSynchronised: data.isDeviceSynchronised,
		};
	}
}

interface OfflineKeyResponse {
	id: number;
	key: {
		id: number;
		serialNumber?: string;
		name: string;
	};
	deviceHeader: {
		id: number;
		hardwareId: string;
		name: string;
	};
	isDeviceSynchronised: boolean;
}

export default OfflineAccessService;
